<template>
  <Teleport to="body">
    <div
      v-if="productKey && learnCopy"
      class="learn-overlay open"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div class="learn-modal">
        <div class="learn-modal-top">
          <div class="learn-label">{{ learnCopy.label }}</div>
          <button type="button" class="learn-close" aria-label="Close" @click="close">&times;</button>
        </div>
        <div class="learn-scroll">
          <h2 class="learn-headline">{{ learnCopy.headline }}</h2>
          <p class="learn-desc">{{ learnCopy.desc }}</p>
          <div class="learn-stats" :class="learnCopy.statsClass">
            <div v-for="(stat, idx) in learnCopy.stats" :key="idx" class="learn-stat">
              <div class="num" :class="{ blue: stat.blue }">{{ stat.num }}</div>
              <div class="txt">{{ stat.txt }}</div>
            </div>
          </div>
          <div class="learn-features">
            <div v-for="(feat, idx) in learnCopy.features" :key="idx" class="learn-feat">
              <i class="fas" :class="feat.icon"></i>
              <div>
                <strong v-if="feat.title">{{ feat.title }}</strong>
                <template v-if="feat.title"> — </template>
                {{ feat.body }}
              </div>
            </div>
          </div>
        </div>
        <div class="learn-footer" :class="{ 'form-open': contactOpen, 'thanks-open': thanksOpen }">
          <div class="learn-footer-actions">
            <button type="button" class="learn-btn ghost" @click="close">Close</button>
            <button type="button" class="learn-btn primary" @click="contactOpen = true">Talk to us</button>
          </div>
          <form class="learn-contact-form" @submit.prevent="sendInterest">
            <h3>Talk with us about {{ learnCopy.label }}</h3>
            <p class="form-hint">We’ll open an email to asya@accessiq.health with your details.</p>
            <div class="learn-field">
              <label for="learn-company">Company</label>
              <input id="learn-company" v-model="contact.company" required>
            </div>
            <div class="learn-field">
              <label for="learn-name">Name</label>
              <input id="learn-name" v-model="contact.name" required>
            </div>
            <div class="learn-field">
              <label for="learn-email">Email</label>
              <input id="learn-email" v-model="contact.email" type="email" required>
            </div>
            <div class="learn-field">
              <label for="learn-phone">Phone</label>
              <input id="learn-phone" v-model="contact.phone" required>
            </div>
            <div class="learn-form-actions">
              <button type="button" class="learn-btn ghost" @click="contactOpen = false">Back</button>
              <button type="submit" class="learn-btn primary">Send to Asya</button>
            </div>
          </form>
          <div class="learn-form-thanks">
            <h3>Thanks — Asya will follow up</h3>
            <p>Your message is ready in your email app, addressed to <strong>asya@accessiq.health</strong>.</p>
            <button type="button" class="learn-btn primary" @click="close">Done</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import auth from '@/services/auth'
import { assumedOrgState } from '@/services/assumedOrg'
import { LEARN_COPY } from '@/data/productWorkspaceCatalog'

const props = defineProps({
  productKey: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['close'])

const contactOpen = ref(false)
const thanksOpen = ref(false)
const contact = reactive({
  company: '',
  name: '',
  email: '',
  phone: '',
})

const learnCopy = computed(() => (props.productKey ? LEARN_COPY[props.productKey] : null))

function effectiveOrg() {
  return assumedOrgState.org || auth.getUserOrganization()
}

function primeContact() {
  const org = effectiveOrg()
  const user = auth.getUser() || {}
  contact.company = org?.display_name || org?.name || ''
  contact.name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || ''
  contact.email = user.email || ''
  contact.phone = ''
  contactOpen.value = false
  thanksOpen.value = false
}

watch(
  () => props.productKey,
  (key) => {
    if (key) primeContact()
  },
)

function close() {
  emit('close')
}

function sendInterest() {
  const productLabel = learnCopy.value?.label || 'AccessIQ product'
  const subject = `Product interest: ${productLabel}`
  const body = [
    'Hi Asya,',
    '',
    `Someone requested a conversation about ${productLabel} from the AccessIQ Workspace.`,
    '',
    `Company: ${contact.company.trim()}`,
    `Name: ${contact.name.trim()}`,
    `Email: ${contact.email.trim()}`,
    `Phone: ${contact.phone.trim()}`,
    '',
    '— AccessIQ Workspace',
  ].join('\n')
  window.location.href = `mailto:asya@accessiq.health?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  contactOpen.value = false
  thanksOpen.value = true
}
</script>

<style scoped>
.learn-overlay {
  position: fixed;
  inset: 0;
  z-index: 13000;
  background: rgba(15, 22, 32, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.learn-modal {
  background: #ffffff;
  border-radius: 16px;
  width: min(640px, 100%);
  max-height: min(90vh, 820px);
  display: flex;
  flex-direction: column;
  border: 2px solid #6b7a99;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}

.learn-modal-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 32px 32px 0;
}

.learn-close {
  background: none;
  border: none;
  color: #b0bac9;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
}

.learn-scroll {
  overflow-y: auto;
  padding: 12px 32px 32px;
  flex: 1;
  min-height: 0;
}

.learn-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0059b2;
}

.learn-headline {
  font-size: 32px;
  font-weight: 800;
  color: #212938;
  letter-spacing: -0.5px;
  line-height: 1.22;
  margin: 14px 0 18px;
}

.learn-desc {
  font-size: 17px;
  color: #6b7a99;
  line-height: 1.65;
  margin-bottom: 28px;
}

.learn-stats {
  display: grid;
  gap: 18px;
  margin-bottom: 28px;
}

.learn-stats.cols-2,
.learn-stats.cols-2x2 { grid-template-columns: 1fr 1fr; }
.learn-stats.cols-3 { grid-template-columns: 1fr 1fr 1fr; }

.learn-stat {
  border: 2px solid #6b7a99;
  border-radius: 12px;
  padding: 20px 22px;
}

.learn-stat .num {
  font-size: 34px;
  font-weight: 800;
  color: #212938;
  margin-bottom: 10px;
}

.learn-stat .num.blue { color: #0059b2; }
.learn-stat .txt { font-size: 15px; color: #6b7a99; line-height: 1.5; }

.learn-feat {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 18px 0;
  border-top: 1px solid #d9e2ef;
  font-size: 16px;
  color: #212938;
  line-height: 1.55;
}

.learn-feat:first-child { border-top: none; padding-top: 8px; }
.learn-feat i { color: #0059b2; width: 22px; text-align: center; margin-top: 3px; }

.learn-footer {
  padding: 20px 32px 26px;
  border-top: 1px solid #d9e2ef;
  display: flex;
  gap: 12px;
}

.learn-footer.form-open { flex-direction: column; }
.learn-footer-actions { display: flex; gap: 10px; width: 100%; }
.learn-footer.form-open .learn-footer-actions,
.learn-footer.thanks-open .learn-footer-actions,
.learn-footer.thanks-open .learn-contact-form { display: none; }

.learn-contact-form {
  display: none;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.learn-footer.form-open .learn-contact-form { display: flex; }

.learn-contact-form h3 { font-size: 15px; font-weight: 700; color: #212938; margin: 0; }
.form-hint { font-size: 12px; color: #6b7a99; margin: -4px 0 2px; }

.learn-field { display: flex; flex-direction: column; gap: 5px; }
.learn-field label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7a99;
}

.learn-field input {
  border: 1.5px solid #6b7a99;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #212938;
}

.learn-form-actions { display: flex; gap: 10px; }
.learn-form-actions .learn-btn { flex: 1; }

.learn-form-thanks { display: none; text-align: center; }
.learn-footer.thanks-open .learn-form-thanks { display: block; }
.learn-form-thanks h3 { font-size: 16px; font-weight: 700; color: #212938; margin-bottom: 6px; }
.learn-form-thanks p { font-size: 13px; color: #6b7a99; margin-bottom: 14px; }

.learn-btn {
  flex: 1;
  padding: 14px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  border: none;
}

.learn-btn.ghost {
  background: #ffffff;
  border: 1.5px solid #d9e2ef;
  color: #212938;
}

.learn-btn.primary { background: #0059b2; color: #fff; }
.learn-btn.primary:hover { background: #0073e6; }

@media (max-width: 900px) {
  .learn-stats.cols-2x2,
  .learn-stats.cols-2,
  .learn-stats.cols-3 { grid-template-columns: 1fr; }
}
</style>
